import React, { useState, useRef, useEffect } from 'react';
import cambioEstadoService from "../../services/cambioEstadoService";
import { obtenerSiguienteEstadoART } from "../../services/art/artEstadoService";
import { toast } from "react-toastify";
import { obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo, subirArchivo } from "../../services/archivoService";
import { getSiniestrosPorTramite } from "../../services/art/siniestroService";
import { getComentariosPorSiniestro } from "../../services/art/comentarioSiniestroService";
import { Box, Typography } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';

function GestorRespuestaART({ tramite, onAvanzar }) {
    const [cargando, setCargando] = useState(false);
    const [observacionesGenerales, setObservacionesGenerales] = useState("");
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    const [todosAprobados, setTodosAprobados] = useState(false);
    const [cargandoSiniestros, setCargandoSiniestros] = useState(true);

    useEffect(() => {
        cargarArchivos();
        verificarSiniestrosAprobados();
    }, [tramite.idTramite]);

    const verificarSiniestrosAprobados = async () => {
        setCargandoSiniestros(true);
        try {
            const siniestros = await getSiniestrosPorTramite(tramite.idTramite);
            if (!siniestros || siniestros.length === 0) {
                setTodosAprobados(false);
                return;
            }

            const comentariosPorSiniestro = await Promise.all(
                siniestros.map(s => getComentariosPorSiniestro(s.idSiniestro).catch(() => []))
            );

            const aprobados = comentariosPorSiniestro.every((comentarios) => {
                if (!Array.isArray(comentarios) || comentarios.length === 0) return false;
                const relevantes = comentarios.filter(c => {
                    const etiqueta = (c?.etiqueta || '').toUpperCase();
                    return etiqueta === 'APROBADO' || etiqueta === 'RECHAZADO';
                });
                if (relevantes.length === 0) return false;
                const ultimo = [...relevantes].sort((a, b) => new Date(b.fecha) - new Date(a.fecha))[0];
                return (ultimo?.etiqueta || '').toUpperCase() === 'APROBADO';
            });

            setTodosAprobados(aprobados);
        } catch (error) {
            console.error('Error al verificar siniestros:', error);
            setTodosAprobados(false);
        } finally {
            setCargandoSiniestros(false);
        }
    };

    const cargarArchivos = async () => {
        setCargandoArchivos(true);
        try {
            const archivosBackend = await obtenerArchivosPorTramite(tramite.idTramite);
            const archivosMapeados = archivosBackend.map(archivo => ({
                id: `backend_${archivo.idArchivo}`,
                idArchivo: archivo.idArchivo,
                nombre: archivo.nombre,
                tamaño: (archivo.tamaño / (1024 * 1024)).toFixed(2) + " MB",
                estado: "subido",
                esDelBackend: true
            }));
            setArchivos(archivosMapeados);
        } catch (error) {
            console.error("Error cargando archivos:", error);
            toast.error("No se pudieron cargar los archivos adjuntos.");
        } finally {
            setCargandoArchivos(false);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (e) => {
        const archivosSeleccionados = Array.from(e.target.files).map((file, idx) => ({
            id: `local_${Date.now()}_${idx}`,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            archivo: file,
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...archivosSeleccionados]);
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');

        if (archivosPendientes.length === 0) {
            toast.warn('No hay archivos pendientes para subir');
            return;
        }

        setSubiendoArchivos(true);
        try {
            for (const archivo of archivosPendientes) {
                try {
                    await subirArchivo(tramite.idTramite, archivo.archivo);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'subido' } : a)
                    );
                } catch (error) {
                    console.error(`Error subiendo ${archivo.nombre}:`, error);
                    setArchivos(prev =>
                        prev.map(a => a.id === archivo.id ? { ...a, estado: 'error' } : a)
                    );
                }
            }
            toast.success('Archivos procesados');
        } catch (error) {
            console.error('Error al subir archivos:', error);
            toast.error('Error al subir algunos archivos');
        } finally {
            setSubiendoArchivos(false);
        }
    };

    const handleDescargarArchivo = async (archivo) => {
        try {
            await descargarArchivo(archivo.idArchivo, archivo.nombre);
        } catch (error) {
            toast.error('Error al descargar archivo');
        }
    };

    const handleEliminarArchivo = async (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (!archivo) return;

        if (archivo.esDelBackend && archivo.idArchivo) {
            try {
                await eliminarArchivo(archivo.idArchivo);
                setArchivos(prev => prev.filter(a => a.id !== id));
                toast.success('Archivo eliminado');
            } catch (error) {
                toast.error('Error al eliminar archivo');
            }
        } else {
            setArchivos(prev => prev.filter(a => a.id !== id));
        }
    };

    const handleFinalizarTramite = async () => {
        setCargando(true);
        try {
            const estadoFinalizado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, 'Finalizado');

            if (!estadoFinalizado) {
                toast.error('No se pudo obtener el estado Finalizado.');
                return;
            }

            const comentarioFinal = [
                'Todos los siniestros fueron aprobados por la ART.',
                'Trámite cerrado sin pasar a Comisión Médica.',
                observacionesGenerales.trim() ? `Observaciones: ${observacionesGenerales.trim()}` : null
            ].filter(Boolean).join('\n');

            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: estadoFinalizado.id || estadoFinalizado.idEstado,
                comentario: comentarioFinal
            });

            toast.success('Todos los siniestros aprobados. Trámite finalizado.');
            onAvanzar();
        } catch (error) {
            console.error('Error al finalizar trámite:', error);
            toast.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setCargando(false);
        }
    };

    const handleAvanzarAComision = async () => {
        const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

        if (archivosPendientes) {
            toast.error('Debe subir todos los archivos pendientes primero');
            return;
        }

        setCargando(true);
        try {
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, "Pendiente de Anexo");

            if (!siguienteEstado) {
                toast.error("No se pudo obtener el estado destino: Pendiente de Anexo.");
                setCargando(false);
                return;
            }

            const comentarioFinal = observacionesGenerales.trim()
                ? `Observaciones de Respuesta ART:\n${observacionesGenerales}`
                : `Respuesta ART registrada sin observaciones adicionales.`;

            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: siguienteEstado.id || siguienteEstado.idEstado,
                comentario: comentarioFinal
            });

            toast.success("Respuesta ART registrada. Avanzando a Pendiente de Anexo.");
            onAvanzar();
        } catch (error) {
            console.error("Error al avanzar a Comisión Médica:", error);
            toast.error("Error: " + (error.response?.data?.message || error.message));
        } finally {
            setCargando(false);
        }
    };

    const archivosPendientes = archivos.some(a => a.estado === 'pendiente');

    const bannerRespuesta = (() => {
        if (!tramite?.fechaEstadoActual) return null;
        const fechaRespuesta = new Date(tramite.fechaEstadoActual);
        if (isNaN(fechaRespuesta.getTime())) return null;

        fechaRespuesta.setHours(0, 0, 0, 0);
        const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
        const meses = Math.floor(
            (hoy.getFullYear() - fechaRespuesta.getFullYear()) * 12 +
            (hoy.getMonth() - fechaRespuesta.getMonth())
        );

        // Solo mostrar banner a partir de 1 mes
        if (meses < 1) return null;

        const textoElapsed = `Recibida hace ${meses} mes${meses !== 1 ? 'es' : ''}`;
        const scheme = meses >= 3
            ? { header: '#7f1d1d', borde: '#dc2626', badge: '#dc2626', sub: '#fca5a5', alerta: 'Trámite sin avance — requiere atención urgente' }
            : meses >= 2
                ? { header: '#7c2d12', borde: '#ea580c', badge: '#ea580c', sub: '#fdba74', alerta: 'Sin movimiento por 2 meses — revisar estado del expediente' }
                : { header: '#78350f', borde: '#f59e0b', badge: '#f59e0b', sub: '#fcd34d', alerta: 'Pendiente de avance — respuesta ART sin gestionar' };

        return (
            <Box sx={{
                background: scheme.header,
                border: `2px solid ${scheme.borde}`,
                borderRadius: 3, p: 3, mb: 3,
            }}>
                <Typography variant="overline" sx={{ color: scheme.sub, letterSpacing: 2, fontSize: '0.7rem', display: 'block' }}>
                    RESPUESTA ART RECIBIDA EL
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.3, mt: 0.5, textTransform: 'capitalize' }}>
                    {fechaRespuesta.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                    <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: 2, px: 2, py: 0.5 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 700 }}>
                            <i className="bi bi-clock-history me-2"></i>{textoElapsed}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: scheme.sub, fontWeight: 500 }}>
                        {scheme.alerta}
                    </Typography>
                </Box>
            </Box>
        );
    })();

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            {bannerRespuesta}
            {/* --- HEADER --- */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Respuesta de la ART</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <AssignmentTurnedInIcon fontSize="small" className="me-1" />
                    Gestión Centralizada
                </span>
            </div>

            {/* --- INFORMACIÓN CONTEXTUAL --- */}
            <div className="alert alert-info border-0 rounded-3 mb-4">
                <div className="text-center mb-3">
                    <p className="mb-0 mt-2">
                        <strong style={{ color: '#155724', fontSize: '1rem' }}>
                            ✓ SE HABILITÓ LA OPCIÓN DE APROBAR O RECHAZAR CADA SINIESTRO INDIVIDUALMENTE
                        </strong>
                        <br/>
                    </p>
                </div>
                <div className="mt-3 pt-3 border-top border-info-subtle">
                    <strong>Información:</strong>
                    <p className="mb-0 mt-1">
                        Registre las observaciones generales sobre la respuesta de la aseguradora.
                        Opcionalmente, adjunte documentos relacionados (notificaciones, actas de aceptación/rechazo, etc.).
                    </p>
                </div>
            </div>

            {/* --- SECCIÓN DE OBSERVACIONES GENERALES --- */}
            <div className="mb-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <DescriptionIcon fontSize="small" className="me-2 mb-1" />
                    Observaciones Generales
                </h6>
                <textarea
                    className="form-control"
                    rows="6"
                    placeholder="Ingrese observaciones sobre la respuesta general de la ART, decisiones tomadas, o notas importantes para el expediente..."
                    value={observacionesGenerales}
                    onChange={(e) => setObservacionesGenerales(e.target.value)}
                    disabled={cargando}
                    style={{ borderRadius: '0.5rem',
                        border: '1px solid #e0e0e0',
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", 
                        lineHeight: '1.5' }}
                />
                <small className="text-muted mt-2 d-block">
                    Estas observaciones se guardarán en el historial del trámite.
                </small>
            </div>

            {/* --- SECCIÓN DE ARCHIVOS --- */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <AttachFileIcon fontSize="small" className="me-2 mb-1" />
                    Documentos de Respuesta ART
                </h6>

                {cargandoArchivos ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {archivos.length > 0 && (
                            <div className="mb-3">
                                <div className="list-group">
                                    {archivos.map((archivo) => (
                                        <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center rounded-3 mb-2">
                                            <div className="d-flex align-items-center flex-grow-1">
                                                {archivo.nombre.endsWith('.pdf') ? (
                                                    <PictureAsPdfIcon className="text-danger me-2" />
                                                ) : (
                                                    <AttachFileIcon className="text-muted me-2" />
                                                )}
                                                <div>
                                                    <strong>{archivo.nombre}</strong>
                                                    <small className="text-muted ms-2">({archivo.tamaño})</small>
                                                    {archivo.estado === 'pendiente' && (
                                                        <span className="badge bg-warning text-dark ms-2">Pendiente</span>
                                                    )}
                                                    {archivo.estado === 'error' && (
                                                        <span className="badge bg-danger ms-2">Error</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="btn-group ms-2" role="group">
                                                {archivo.esDelBackend && (
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => handleDescargarArchivo(archivo)}
                                                        title="Descargar"
                                                    >
                                                        <DownloadIcon fontSize="small" />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleEliminarArchivo(archivo.id)}
                                                    title="Eliminar"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div
                            className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                            style={{ cursor: 'pointer', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#cfe2ff' }}
                        >
                            <input type="file" multiple ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                            <CloudUploadIcon className="text-muted mb-2" style={{ fontSize: '2.5rem', display: 'block', margin: '0 auto' }} />
                            <p className="mb-0">Arrastra archivos aquí o haz clic para adjuntar notificaciones y actas</p>
                        </div>

                        {archivosPendientes && (
                            <div className="d-flex justify-content-center mt-3">
                                <button className="btn btn-primary rounded-pill px-4" onClick={handleSubirArchivos} disabled={subiendoArchivos}>
                                    {subiendoArchivos ? 'Subiendo...' : `Subir ${archivos.filter(a => a.estado === 'pendiente').length} archivo(s)`}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* --- ACCIONES DE SALIDA --- */}
            <div className="mt-4 pt-3 border-top">
                {/* Indicador de carga / verificación */}
                {cargandoSiniestros && (
                    <p className="text-center text-muted small mb-3">
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Verificando estado de siniestros...
                    </p>
                )}

                {/* Banner: todos los siniestros aprobados */}
                {!cargandoSiniestros && todosAprobados && (
                    <div className="alert alert-success border-0 rounded-3 mb-3 d-flex align-items-start gap-3">
                        <CheckCircleIcon className="text-success mt-1 flex-shrink-0" />
                        <div>
                            <strong>¡Todos los siniestros fueron aprobados por la ART!</strong>
                            <p className="mb-0 mt-1 small">
                                El trámite puede cerrarse directamente sin pasar a Comisión Médica.
                            </p>
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-center flex-wrap gap-3">
                    {!cargandoSiniestros && todosAprobados && (
                        <button
                            className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                            onClick={handleFinalizarTramite}
                            disabled={cargando || subiendoArchivos || archivosPendientes}
                            title="Todos aprobados — cerrar el trámite"
                        >
                            {cargando ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                                <CheckCircleIcon fontSize="small" />
                            )}
                            Todos aprobados → Finalizar Trámite
                        </button>
                    )}

                    <button
                        className="btn btn-outline-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={handleAvanzarAComision}
                        disabled={cargando || subiendoArchivos || archivosPendientes}
                        title={archivosPendientes ? "Debe subir todos los archivos pendientes primero" : "Avanzar a Pendiente de Anexo"}
                    >
                        {cargando ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Procesando...
                            </>
                        ) : (
                            <>
                                <SendIcon fontSize="small" />
                                Ir a Pendiente de Anexo
                            </>
                        )}
                    </button>
                </div>
                {!cargandoSiniestros && !todosAprobados && (
                    <p className="text-center mt-3 mb-0">
                        <button
                            className="btn btn-link btn-sm text-muted p-0"
                            onClick={verificarSiniestrosAprobados}
                            disabled={cargandoSiniestros}
                        >
                            Actualizar verificación de siniestros
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}

export default GestorRespuestaART;

